import { useSyncExternalStore } from 'react';

export interface Store<S> {
  state: S;
  setState: (newState: Partial<S>, afterUpdate?: () => void) => void;
  actions: object;
}

/**
 * An action receives the store as its first argument; the bound version
 * does not. Actions can be grouped in nested objects. Annotate the extra
 * parameters (`(store, amount: number) => ...`): they are what the bound
 * action accepts.
 */
type Action<S> = (store: Store<S>, ...args: never[]) => unknown;

interface ActionGroup<S> {
  [name: string]: Action<S> | ActionGroup<S>;
}

type BoundActions<S, A> = {
  [K in keyof A]: A[K] extends (store: Store<S>, ...args: infer P) => infer R
    ? (...args: P) => R
    : BoundActions<S, A[K]>;
};

/**
 * Bind actions to the store, recursing into nested action groups.
 *
 * @param {object} store
 * @param {object} actions
 *
 * @return {object}
 */
function bindActions<S>(store: Store<S>, actions: object): object {
  const bound: Record<string, unknown> = {};
  const groups = actions as Record<string, unknown>;
  Object.keys(groups).forEach(key => {
    const action = groups[key];
    if (typeof action === 'function') {
      bound[key] = action.bind(null, store);
    } else if (action && typeof action === 'object') {
      bound[key] = bindActions(store, action);
    }
  });
  return bound;
}

/**
 * Create a hook that shares one state object between all components.
 *
 * Every action receives the store as its first argument. `store.state` holds
 * the current state and `store.setState(partial)` merges `partial` into it and
 * re-renders every component that uses the hook.
 *
 * @param {object|null} initialState
 * @param {object} actions
 *
 * @return {function(): [object, object]} Hook returning `[state, actions]`.
 */
export default function globalHook<
  S extends object | null,
  A extends ActionGroup<S> = Record<never, never>,
>(initialState: S, actions: A = {} as A) {
  const listeners = new Set<() => void>();
  const store = { state: initialState } as Store<S>;

  store.setState = (newState, afterUpdate) => {
    store.state = { ...store.state, ...newState } as S;
    listeners.forEach(listener => listener());
    if (afterUpdate) afterUpdate();
  };
  store.actions = bindActions(store, actions);

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };
  const getSnapshot = () => store.state;

  return function useGlobalState() {
    const state = useSyncExternalStore(subscribe, getSnapshot);
    return [state, store.actions as BoundActions<S, A>] as const;
  };
}
